/**
 * UUID v4 generation that also works outside a secure context.
 *
 * `crypto.randomUUID` is only exposed on secure origins (https, or localhost).
 * Production runs over plain http on a VM, where the property is simply absent
 * and calling it throws `crypto.randomUUID is not a function`. These ids are
 * client-side correlation keys - pending-send keys and upload ids - so a
 * fallback is fine as long as collisions stay vanishingly unlikely.
 *
 * `crypto.getRandomValues` is NOT secure-context gated, so it is the first
 * fallback; `Math.random` is the last resort for exotic environments.
 */
export function randomId(): string {
  const webCrypto: Crypto | undefined = globalThis.crypto;

  if (typeof webCrypto?.randomUUID === 'function') {
    return webCrypto.randomUUID();
  }

  const bytes = new Uint8Array(16);

  if (typeof webCrypto?.getRandomValues === 'function') {
    webCrypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  // Version 4, variant 10xx - per RFC 4122.
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join('-');
}
